import { useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { useVenueBusinessInfo } from '@/hooks/useVenueBusinessInfo'
import { formatDateTime } from '@/lib/venue/format'
import { hasPendingBusinessInfoChangeRequest } from '@/lib/venue/venueBusinessInfoViewModel'
import { useVenueContext } from '@/routes/venueContext'

/**
 * 사업자 정보 현황 — 현행 사업자등록번호·서류 + 대기중 수정 요청 + 최근 반려 사유(#1191, 요구사항 3).
 *
 * 조회 전용 화면이다. 수정은 `business-info/edit`(별도 화면)으로 이동한다 — `VenueProfileSummaryPage`/
 * `VenueProfileEditPage`의 기존 요약-편집 분리 관례를 따른다(design.md §2 자율 결정).
 * 🚨 이 화면에는 인증 상태·기한·노출·예약 필드를 표시하지 않는다 — 그 축은 `operation-verification/status`
 * 가 답한다. 두 축을 한 화면에 섞으면 "수정 요청 심사 중 = 인증 대기 중"으로 오독한다(design.md §2 (2)).
 */
export function VenueBusinessInfoPage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()
  const infoQuery = useVenueBusinessInfo(venueId)

  return (
    <>
      <PageHeader title="사업자 정보" description="구장 운영 인증에 등록된 사업자등록번호·사업자등록증을 확인합니다." />

      {infoQuery.isLoading ? <LoadingState rows={3} label="사업자 정보를 불러오는 중" /> : null}

      {infoQuery.isError ? (
        <ErrorState
          error={infoQuery.error}
          onRetry={() => {
            void infoQuery.refetch()
          }}
        />
      ) : null}

      {infoQuery.data ? (
        <div className="space-y-5">
          {infoQuery.data.latestRejection ? (
            <Alert tone="danger" title="최근 수정 요청이 반려되었습니다">
              {infoQuery.data.latestRejection.reason}
              <span className="mt-1 block text-xs text-slateish-500">
                반려 시각 {formatDateTime(infoQuery.data.latestRejection.reviewedAt)} — 기존 사업자 정보는 그대로
                유지됩니다.
              </span>
            </Alert>
          ) : null}

          {infoQuery.data.pendingRequest ? (
            <Alert tone="warning" title="수정 요청 심사 중">
              제출한 내용을 운영자가 검토하고 있어요. 승인되면 아래 사업자 정보가 새 값으로 바뀝니다.
            </Alert>
          ) : null}

          <Card>
            <CardHeader
              title="현재 등록된 사업자 정보"
              action={
                <Button
                  size="sm"
                  disabled={hasPendingBusinessInfoChangeRequest(infoQuery.data)}
                  title={
                    hasPendingBusinessInfoChangeRequest(infoQuery.data)
                      ? '심사 중인 수정 요청이 있어 새 요청을 등록할 수 없습니다.'
                      : undefined
                  }
                  onClick={() => {
                    navigate(`/venues/${venueId}/business-info/edit`)
                  }}
                >
                  정보 수정
                </Button>
              }
            />
            <CardBody className="space-y-3">
              {infoQuery.data.current ? (
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-slateish-500">사업자등록번호</dt>
                    <dd className="mt-0.5 text-sm font-medium text-ink-700">{infoQuery.data.current.businessNumber}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slateish-500">사업자등록증</dt>
                    <dd className="mt-0.5 text-sm font-medium text-ink-700">
                      {infoQuery.data.current.documentFileName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slateish-500">적용일</dt>
                    <dd className="mt-0.5 text-sm font-medium text-ink-700">
                      {formatDateTime(infoQuery.data.current.effectiveAt)}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-slateish-600">아직 승인된 사업자 정보가 없습니다.</p>
              )}
            </CardBody>
          </Card>

          {infoQuery.data.pendingRequest ? (
            <Card>
              <CardHeader title="심사 대기 중인 요청" action={<Badge tone="warning">대기 중</Badge>} />
              <CardBody className="space-y-3">
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-slateish-500">요청한 사업자등록번호</dt>
                    <dd className="mt-0.5 text-sm font-medium text-ink-700">
                      {infoQuery.data.pendingRequest.newBusinessNumber}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slateish-500">요청한 사업자등록증</dt>
                    <dd className="mt-0.5 text-sm font-medium text-ink-700">
                      {infoQuery.data.pendingRequest.newDocumentFileName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slateish-500">요청 시각</dt>
                    <dd className="mt-0.5 text-sm font-medium text-ink-700">
                      {formatDateTime(infoQuery.data.pendingRequest.requestedAt)}
                    </dd>
                  </div>
                </dl>
                {infoQuery.data.pendingRequest.requestReason ? (
                  <p className="text-sm text-slateish-600">사유: {infoQuery.data.pendingRequest.requestReason}</p>
                ) : null}
              </CardBody>
            </Card>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
